import React, { useState, useEffect, useCallback, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import bg from '../images/livesession/bg.png';
import Card from '../images/livesession/card.png';
import Card1 from '../images/livesession/card1.png';
import hand from '../images/livesession/hands.png';
import Solar from '../images/livesession/solar_calendar-bold.png';
import Location from '../images/livesession/location.png';
import './livesession.css';

gsap.registerPlugin(ScrollTrigger);

const AUCTION_ITEMS = [
  {
    id: 'ohtani',
    image: Card,
    name: 'Shohei Ohtani',
    badge: 'Limited Edition',
    currentBid: 12500,
  },
  {
    id: 'rousey',
    image: Card1,
    name: 'Ronda Rousey',
    badge: 'Limited Edition',
    currentBid: 18200,
  },
];

const AUCTION_STATS = [
  { label: 'Lot', value: '018' },
  { label: 'Status', value: 'LIVE' },
  { label: 'Bids', value: '21' },
  { label: 'Watchers', value: '89' },
  { label: 'Min Increment', value: '$50' },
  { label: 'Closing', value: 'Soon' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);

function useCountdown(initialSeconds) {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;

    const timerId = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timerId);
  }, [secondsLeft]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return {
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
  };
}

function AuctionCard({ item }) {
  return (
    <div className="auction-card">
      <img src={item.image} alt={item.name} className="auction-card__image" />
      <h3 className="auction-card__name">{item.name}</h3>
      <span className="auction-card__badge">{item.badge}</span>
      <div className="auction-card__bid">
        <span className="auction-card__bid-amount">{formatCurrency(item.currentBid)}</span>
        <span className="auction-card__bid-label">Current Bid</span>
      </div>
    </div>
  );
}

function Livesession() {
  const { minutes, seconds } = useCountdown(10 * 60 + 62);
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const arenaRef = useRef(null);
  const statsRef = useRef(null);

  const eventDate = new Date('2026-06-15').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 78%',
          once: true,
        },
        defaults: { ease: 'power3.out' },
      });

      tl.from(headerRef.current.children, {
        opacity: 0,
        y: 22,
        duration: 0.55,
        stagger: 0.08,
      })
        .from(
          arenaRef.current.children,
          {
            opacity: 0,
            y: 30,
            scale: 0.98,
            duration: 0.65,
            stagger: 0.08,
          },
          '-=0.2'
        )
        .from(
          statsRef.current.children,
          {
            opacity: 0,
            y: 18,
            duration: 0.45,
            stagger: 0.08,
          },
          '-=0.15'
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    // FIX: background now lives on the full-width <section>, the grid
    // content (header, arena, stats) is wrapped in .live-session__container
    // which carries max-width: 1760px. Same pattern as Auctionsdetails.
<div className="amin">
      <section className="live-session" style={{ backgroundImage: `url(${bg})` }} ref={sectionRef}>
      <div className="live-session__container">
        <div className="live-session__header" ref={headerRef}>
          <div className="live-session__title">
            <img src={hand} alt="" className="live-session__title-icon" />
            <h2>Tonight's Auction</h2>
          </div>

          <div className="live-session__badge">
            <span className="live-session__dot" />
            LIVE
          </div>

          <div className="live-session__meta">
            <span className="live-session__meta-item">
              <img src={Solar} alt="" />
              {eventDate}
            </span>
            <span className="live-session__meta-item">
              <img src={Location} alt="" />
              OBC Live Arena
            </span>
          </div>
        </div>

        <div className="live-session__arena" ref={arenaRef}>
          <AuctionCard item={AUCTION_ITEMS[0]} />

          <div className="live-session__timer">
            <span className="live-session__timer-label">Time Remaining</span>
            <div className="live-session__timer-display">
              <span>{minutes}</span>
              <span className="live-session__timer-colon">:</span>
              <span>{seconds}</span>
            </div>
            <div className="live-session__timer-units">
              <span>MM</span>
              <span>:</span>
              <span>SS</span>
            </div>
            <span className="live-session__timer-tag">Countdown</span>
          </div>

          <AuctionCard item={AUCTION_ITEMS[1]} />
        </div>

        <div className="live-session__stats" ref={statsRef}>
          {AUCTION_STATS.map((stat) => (
            <div key={stat.label} className="live-session__stat">
              <span className="live-session__stat-label">{stat.label}</span>
              <span className="live-session__stat-value">{stat.value}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
</div>
  );
}

export default Livesession;